/* ==========================================================================
   MoonBrew — <mb-line-chart>
   Draws the illustrative overnight-cortisol comparison from JSON config.
   The equivalent data table is rendered server-side in Liquid and is always
   present in the DOM, so the content survives with JS disabled.
   ========================================================================== */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  function catmullRom(points) {
    if (!points.length) return '';
    var d = 'M' + points[0][0] + ',' + points[0][1];
    for (var i = 0; i < points.length - 1; i++) {
      var p0 = points[i - 1] || points[i];
      var p1 = points[i];
      var p2 = points[i + 1];
      var p3 = points[i + 2] || p2;
      var c1x = p1[0] + (p2[0] - p0[0]) / 6;
      var c1y = p1[1] + (p2[1] - p0[1]) / 6;
      var c2x = p2[0] - (p3[0] - p1[0]) / 6;
      var c2y = p2[1] - (p3[1] - p1[1]) / 6;
      d += 'C' + c1x + ',' + c1y + ' ' + c2x + ',' + c2y + ' ' + p2[0] + ',' + p2[1];
    }
    return d;
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  class MBLineChart extends HTMLElement {}

  MBLineChart.prototype.connectedCallback = function () {
    if (this.mbReady) return;
    this.mbReady = true;

    var configEl = this.querySelector('[data-mb-config]');
    this.svg = this.querySelector('[data-mb-svg]');
    if (!configEl || !this.svg) return;
    try {
      this.config = JSON.parse(configEl.textContent);
    } catch (e) {
      return;
    }
    if (!this.config.series || !this.config.series.length) return;

    this.W = 760;
    this.H = 330;
    this.L = 54;
    this.R = this.config.labelSeriesInline ? 132 : 18;
    this.T = 22;
    this.B = 44;

    this.draw();
    this.bindHover();
    this.bindTable();
  };

  MBLineChart.prototype.px = function (i) {
    var n = this.config.labels.length - 1 || 1;
    return this.L + (i * (this.W - this.L - this.R)) / n;
  };

  MBLineChart.prototype.py = function (v) {
    return this.T + (1 - v / this.config.max) * (this.H - this.T - this.B);
  };

  MBLineChart.prototype.draw = function () {
    var self = this;
    var cfg = this.config;
    var out = '';

    /* recessive gridlines — the data should carry the eye, not the grid */
    [0, 0.25, 0.5, 0.75, 1].forEach(function (frac) {
      var y = self.py(cfg.max * frac);
      out += '<line x1="' + self.L + '" y1="' + y + '" x2="' + (self.W - self.R) + '" y2="' + y +
        '" stroke="#EFEAF6" stroke-width="1"/>';
    });

    /* axis labels, deliberately qualitative — this is an illustration */
    out += '<text x="' + (self.L - 10) + '" y="' + (self.py(cfg.max) + 4) +
      '" text-anchor="end" font-size="11" fill="#8C85A0">' + esc(cfg.highLabel) + '</text>';
    out += '<text x="' + (self.L - 10) + '" y="' + (self.py(0) + 4) +
      '" text-anchor="end" font-size="11" fill="#8C85A0">' + esc(cfg.lowLabel) + '</text>';
    var midY = (self.py(cfg.max) + self.py(0)) / 2;
    out += '<text x="14" y="' + midY + '" font-size="11" fill="#8C85A0" text-anchor="middle" ' +
      'transform="rotate(-90 14 ' + midY + ')">' + esc(cfg.yAxisLabel) + '</text>';

    /* highlight band */
    if (cfg.band && cfg.band.from != null && cfg.band.to != null) {
      var x1 = self.px(cfg.band.from);
      var x2 = self.px(cfg.band.to);
      out += '<rect x="' + x1 + '" y="' + self.T + '" width="' + (x2 - x1) + '" height="' +
        (self.H - self.T - self.B) + '" fill="' + esc(cfg.band.fill || '#FCEFF4') + '"/>';
      if (cfg.band.label) {
        out += '<text x="' + (x1 + x2) / 2 + '" y="' + (self.T + 16) +
          '" text-anchor="middle" font-size="10.5" font-weight="700" fill="#B03C68">' +
          esc(cfg.band.label) + '</text>';
      }
    }

    /* x axis */
    out += '<line x1="' + self.L + '" y1="' + self.py(0) + '" x2="' + (self.W - self.R) + '" y2="' +
      self.py(0) + '" stroke="#DDD5EA" stroke-width="1"/>';
    cfg.labels.forEach(function (label, i) {
      out += '<text x="' + self.px(i) + '" y="' + (self.py(0) + 22) +
        '" text-anchor="middle" font-size="11.5" fill="#655C7A">' + esc(label) + '</text>';
    });

    /* series — 2px strokes, labelled directly at the line end where there's room */
    cfg.series.forEach(function (s) {
      var pts = s.values.map(function (v, i) { return [self.px(i), self.py(v)]; });
      out += '<path d="' + catmullRom(pts) + '" fill="none" stroke="' + esc(s.color) +
        '" stroke-width="2" stroke-linecap="round"/>';
      var last = pts[pts.length - 1];
      out += '<circle cx="' + last[0] + '" cy="' + last[1] + '" r="4.5" fill="' + esc(s.color) +
        '" stroke="#fff" stroke-width="2"/>';
      if (cfg.labelSeriesInline) {
        out += '<text x="' + (last[0] + 12) + '" y="' + (last[1] + 4) +
          '" font-size="12" font-weight="700" fill="' + esc(s.color) + '">' + esc(s.name) + '</text>';
      }
    });

    /* hover affordances */
    out += '<line data-mb-cross x1="0" y1="' + self.T + '" x2="0" y2="' + self.py(0) +
      '" stroke="#B9A5E3" stroke-width="1" stroke-dasharray="3 3" opacity="0"/>';
    cfg.series.forEach(function (s, si) {
      out += '<circle data-mb-hdot="' + si + '" r="5.5" fill="' + esc(s.color) +
        '" stroke="#fff" stroke-width="2" opacity="0"/>';
    });
    out += '<rect data-mb-hit x="' + self.L + '" y="' + self.T + '" width="' +
      (self.W - self.R - self.L) + '" height="' + (self.H - self.T - self.B) + '" fill="transparent"/>';

    this.svg.setAttribute('viewBox', '0 0 ' + this.W + ' ' + this.H);
    this.svg.setAttribute('font-family', 'Inter, system-ui, sans-serif');
    this.svg.innerHTML = out;
  };

  MBLineChart.prototype.bindHover = function () {
    var self = this;
    var hit = this.svg.querySelector('[data-mb-hit]');
    var cross = this.svg.querySelector('[data-mb-cross]');
    var dots = this.svg.querySelectorAll('[data-mb-hdot]');
    var tip = this.querySelector('[data-mb-tip]');
    if (!hit || !tip) return;

    function nearestIndex(clientX) {
      var rect = self.svg.getBoundingClientRect();
      var xv = ((clientX - rect.left) / rect.width) * self.W;
      var best = Infinity;
      var idx = 0;
      self.config.labels.forEach(function (_l, k) {
        var dist = Math.abs(self.px(k) - xv);
        if (dist < best) { best = dist; idx = k; }
      });
      return { idx: idx, rect: rect };
    }

    function show(clientX) {
      var hitInfo = nearestIndex(clientX);
      var i = hitInfo.idx;
      var rect = hitInfo.rect;
      cross.setAttribute('x1', self.px(i));
      cross.setAttribute('x2', self.px(i));
      cross.setAttribute('opacity', '1');
      dots.forEach(function (dot, si) {
        dot.setAttribute('cx', self.px(i));
        dot.setAttribute('cy', self.py(self.config.series[si].values[i]));
        dot.setAttribute('opacity', '1');
      });
      tip.innerHTML = '<b>' + esc(self.config.labels[i]) + '</b>' +
        self.config.series.map(function (s) {
          return '<s style="background:' + esc(s.color) + '"></s>' + esc(s.name) + ': ' + s.values[i];
        }).join('<br>');
      var top = Math.max.apply(null, self.config.series.map(function (s) { return s.values[i]; }));
      tip.style.left = (self.px(i) / self.W) * rect.width + 'px';
      tip.style.top = (self.py(top) / self.H) * rect.height + 'px';
      tip.style.opacity = '1';
    }

    function hide() {
      tip.style.opacity = '0';
      cross.setAttribute('opacity', '0');
      dots.forEach(function (dot) { dot.setAttribute('opacity', '0'); });
    }

    hit.addEventListener('mousemove', function (e) { show(e.clientX); });
    hit.addEventListener('mouseleave', hide);
    hit.addEventListener('touchstart', function (e) { show(e.touches[0].clientX); }, { passive: true });
    hit.addEventListener('touchmove', function (e) { show(e.touches[0].clientX); }, { passive: true });
    hit.addEventListener('touchend', hide);
  };

  MBLineChart.prototype.bindTable = function () {
    var btn = this.querySelector('[data-mb-table-toggle]');
    var table = this.querySelector('[data-mb-table]');
    if (!btn || !table) return;
    var labels = {
      show: btn.dataset.showLabel || 'View as table',
      hide: btn.dataset.hideLabel || 'Hide table'
    };
    btn.addEventListener('click', function () {
      var open = table.getAttribute('data-open') === 'true';
      table.setAttribute('data-open', String(!open));
      btn.textContent = open ? labels.show : labels.hide;
      btn.setAttribute('aria-expanded', String(!open));
    });
  };

  if (!window.customElements.get('mb-line-chart')) {
    window.customElements.define('mb-line-chart', MBLineChart);
  }
})();